import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy, Link2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, FormError, Input } from "@/components/ui/field";
import { Empty, PageHeader, Rows } from "@/features/admin/parts";
import type { Schemas } from "@/lib/api/client";
import {
  invitesQuery,
  registrationQuery,
  useCreateInvite,
  useDeactivateInvite,
  useSetInviteRequired,
} from "@/lib/api/queries";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/invites")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(invitesQuery),
      context.queryClient.ensureQueryData(registrationQuery),
    ]),
  component: InvitesPage,
});

const EXPIRY = [
  { days: 0, label: "Бессрочно" },
  { days: 1, label: "Сутки" },
  { days: 7, label: "Неделя" },
  { days: 30, label: "Месяц" },
] as const;

function inviteLink(code: string): string {
  return `${window.location.origin}/register?code=${code}`;
}

async function copy(text: string, message: string) {
  await navigator.clipboard.writeText(text);
  toast.success(message);
}

function InvitesPage() {
  const { data } = useSuspenseQuery(invitesQuery);

  return (
    <>
      <PageHeader title="Инвайты" />
      <RegistrationSwitch />
      <CreateInviteForm />
      <div className="mt-8">
        {data.length === 0 ? (
          <Empty>Инвайтов ещё не было.</Empty>
        ) : (
          <Rows>
            {data.map((invite) => (
              <InviteRow key={invite.id} invite={invite} />
            ))}
          </Rows>
        )}
      </div>
    </>
  );
}

function RegistrationSwitch() {
  const { data } = useSuspenseQuery(registrationQuery);
  const set = useSetInviteRequired();
  const open = !data.invite_required;

  return (
    <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-4">
      <div>
        <p className="font-medium">Регистрация без кода</p>
        <p className="mt-1 text-sm text-muted">
          {open
            ? "Открыта: зарегистрироваться может любой, кто знает адрес портала."
            : "Закрыта: нужен действующий инвайт."}
        </p>
        <FormError message={set.error?.message} />
      </div>
      <Button
        type="button"
        variant={open ? "secondary" : "primary"}
        pending={set.isPending}
        onClick={() => set.mutate(open)}
      >
        {open ? "Закрыть" : "Открыть"}
      </Button>
    </div>
  );
}

function CreateInviteForm() {
  const create = useCreateInvite();
  const [maxUses, setMaxUses] = useState("1");
  const [days, setDays] = useState<number>(7);
  const [note, setNote] = useState("");

  return (
    <form
      className="mt-6 grid gap-4 rounded-2xl border border-line bg-surface p-4 sm:grid-cols-[6rem_10rem_1fr_auto] sm:items-end"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            max_uses: Number(maxUses),
            expires_at: days ? new Date(Date.now() + days * 86_400_000).toISOString() : null,
            note: note.trim() || null,
          },
          {
            onSuccess: (invite) => {
              setNote("");
              void copy(
                inviteLink(invite.code),
                `Инвайт ${invite.code} создан, ссылка скопирована`,
              );
            },
          },
        );
      }}
    >
      <Field label="Раз">
        {(control) => (
          <Input
            {...control}
            type="number"
            min={1}
            max={100}
            inputMode="numeric"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
        )}
      </Field>
      <Field label="Действует">
        {(control) => (
          <select
            {...control}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-12 w-full rounded-xl border border-line bg-surface px-3 text-base"
          >
            {EXPIRY.map((option) => (
              <option key={option.days} value={option.days}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Заметка">
        {(control) => (
          <Input
            {...control}
            value={note}
            maxLength={200}
            placeholder="Для кого"
            onChange={(e) => setNote(e.target.value)}
          />
        )}
      </Field>
      <Button type="submit" pending={create.isPending}>
        Создать
      </Button>
      <div className="sm:col-span-4">
        <FormError message={create.error?.message} />
      </div>
    </form>
  );
}

function InviteRow({ invite }: { invite: Schemas["InviteOut"] }) {
  const deactivate = useDeactivateInvite();
  return (
    <li className="grid gap-2 px-4 py-3 sm:grid-cols-[9rem_1fr_auto] sm:items-center sm:gap-4">
      <span
        className={cn(
          "font-display text-base tracking-wide",
          !invite.usable && "text-muted line-through",
        )}
      >
        {invite.code}
      </span>
      <span className="text-sm text-muted">
        {invite.note && <span className="text-text">{invite.note}. </span>}
        Использован {invite.uses} из {invite.max_uses}
        {invite.expires_at && `, до ${formatDate(invite.expires_at)}`}
        {!invite.is_active && ", отключён"}
      </span>
      <span className="flex gap-1">
        {invite.usable && (
          <>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Скопировать ссылку"
              onClick={() => copy(inviteLink(invite.code), "Ссылка скопирована")}
            >
              <Link2 aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Скопировать код"
              onClick={() => copy(invite.code, "Код скопирован")}
            >
              <Copy aria-hidden />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              pending={deactivate.isPending}
              onClick={() => deactivate.mutate(invite.id)}
            >
              Отключить
            </Button>
          </>
        )}
      </span>
    </li>
  );
}
